import { Fragment, useEffect, useRef, useState, useMemo } from 'react';
import { FacebookIcon } from '@ui/icons/social/facebook';
import { InstagramIcon } from '@ui/icons/social/instagram';
import { YoutubeIcon } from '@ui/icons/social/youtube';
import { LandingNavbar } from '@app/landing/landing-navbar';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  ShieldCheck, 
  Shield,
  Share2, 
  UserPlus, 
  Upload, 
  Globe2, 
  Lock, 
  Database, 
  Users, 
  Globe, 
  Zap, 
  Wifi, 
  HardDrive, 
  FolderKanban,
  Image as ImageIcon,
  Film,
  IndianRupee,
  Building2,
  Check, 
  X, 
  Key,
  KeyRound,
  Clock,
  Eye,
  FileCheck,
  ShieldOff,
  FolderX,
  CloudUpload
} from 'lucide-react';
import { ArrowLeftIcon } from '@ui/icons/material/ArrowLeftIcon';
import { ArrowRightIcon } from '@ui/icons/material/ArrowRightIcon';
import { BillingCycleRadio } from '@common/billing/pricing-table/billing-cycle-radio';
import { PricingTable } from '@common/billing/pricing-table/pricing-table';
import {
  ContactPagePayload,
  useSubmitContactForm,
} from '@common/contact/use-submit-contact-form';
import { useForm } from 'react-hook-form';
import { Dialog } from '@ui/overlays/dialog/dialog';
import { DialogBody } from '@ui/overlays/dialog/dialog-body';
import { DialogHeader } from '@ui/overlays/dialog/dialog-header';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { FormTextField } from '@ui/forms/input-field/text-field/text-field';
import { useRecaptcha } from '@common/recaptcha/use-recaptcha';
import { Button } from '@ui/buttons/button';
import { Trans } from '@ui/i18n/trans';
import { Form } from '@ui/forms/form';
import { UpsellBillingCycle } from '@common/billing/pricing-table/find-best-price';
import { useProducts } from '@common/billing/pricing-table/use-products';
import { CheckCircleOutlineIcon } from '@ui/icons/material/CheckCircleOutline';
import { FullPageLoader } from '@ui/progress/full-page-loader';
import { useThemeSelector } from '@ui/themes/theme-selector-context';
import { Helmet } from '@common/seo/helmet';
import { softwareSchema } from '@app/seo/seo-schema';

export function BestSecureCloudStorage() {
  const query = useProducts('pricingPage');
  const [selectedCycle, setSelectedCycle] = useState<UpsellBillingCycle>('month');
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [visible, setVisible] = useState(false);
  const reviewRef = useRef<HTMLDivElement | null>(null);

  const { selectThemeTemporarily } = useThemeSelector();
  useEffect(() => {
    selectThemeTemporarily('light');
    return () => selectThemeTemporarily(null);
  }, [selectThemeTemporarily]);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    document.documentElement.classList.add('light');
  }, []);

  // 3 STEPS WORKFLOW
  const steps = [
    {
      icon: UserPlus,
      number: '01',
      title: 'Sign Up in Seconds',
      description: 'Create your secure account instantly with no credit card required and claim 25 GB permanent free online storage.',
    },
    {
      icon: Upload,
      number: '02',
      title: 'Upload Uncapped Files',
      description: 'Upload 50GB+ master video files, RAW photo galleries, and documents with sub-15ms domestic Indian peering.',
    },
    {
      icon: Globe2,
      number: '03',
      title: 'Access Anywhere Safely',
      description: 'Access encrypted files across mobile, desktop, and tablet with zero-knowledge AES-256 privacy and zero AI scraping.',
    },
  ];

  // 5 CORE PAIN POINT PILLARS (SOLVING LEGACY PROBLEMS)
  const cards = [
    {
      icon: <Zap className="w-24 h-24 text-white" />,
      title: "Sub-15ms Line-Speed Uploads",
      desc: (
        <>
          Google Drive and Dropbox stall at 15–20 MB/s on Indian fiber due to 150ms+ overseas routing.{" "}
          <a href="/" target="_blank" rel="noopener noreferrer" className="text-[#0072ff] font-bold hover:underline">
            SpaceByte Cloud
          </a>{" "}
          peers directly with Indian Internet Exchanges to saturate 1 Gbps lines at 110+ MB/s with uncapped file sizes.
        </>
      ),
      color: "from-[#1E3A8A] to-[#3B82F6]",
    },
    {
      icon: <ShieldCheck className="w-24 h-24 text-white" />,
      title: "Zero AI Scraping & DPDP Act Compliance",
      desc: "Big Tech cloud services scan private documents to train generative AI models. SpaceByte guarantees 100% Zero AI Scraping, zero-knowledge AES-256 encryption, and hosting in certified Indian Tier-4 data centers.",
      color: "from-[#3B82F6] to-[#60A5FA]",
    },
    {
      icon: <Film className="w-24 h-24 text-white" />,
      title: "100% Bit-for-Bit RAW Preservation",
      desc: "Avoid lossy background transcoding. SpaceByte never compresses 4K/8K ProRes exports or strips EXIF metadata from camera RAW files (.ARW, .CR3, .DNG), preserving master studio fidelity.",
      color: "from-[#06B6D4] to-[#3B82F6]",
    },
    {
      icon: <IndianRupee className="w-24 h-24 text-white" />,
      title: "No Per-Seat Taxes & Direct UPI Billing",
      desc: "No $15/month per-user licensing taxes or 3.5% foreign exchange credit card markups. SpaceByte offers pooled team capacity billed transparently in Indian Rupees via UPI, RuPay, and local cards.",
      color: "from-[#1E3A8A] to-[#06B6D4]",
    },
    {
      icon: <Share2 className="w-24 h-24 text-white" />,
      title: "Zero Forced Login Client Delivery",
      desc: "Deliver large video projects and asset libraries via password-protected links with custom expiry. Clients download at full gigabit speed without forced Google/Microsoft accounts or quota walls.",
      color: "from-[#3B82F6] to-[#1E3A8A]",
    },
  ];

  // SECURITY FEATURES (ORBITING SHIELD SECTION)
  const securityFeatures = [
    {
      icon: Share2,
      title: 'Password Protected File Sharing',
      description: 'Distribute client deliverables, legal files, and 4K footage with password-protected custom expiry links.',
    },
    {
      icon: KeyRound,
      title: 'Zero-Knowledge AES-256 Encryption',
      description: 'End-to-end encrypted cloud storage where only you hold the keys. Nobody, not even our engineers, can view your files.',
    },
    {
      icon: Clock,
      title: 'Custom Link Expiration & Access Control',
      description: 'Set granular access permissions, download limits, and automated expiry dates for campaign and client deliverables.',
    },
    {
      icon: Zap,
      title: 'Gigabit Fiber Upload Speed',
      description: 'Saturate your connection up to 110+ MB/s without connection timeouts, throttling, or browser crashes on large files.',
    },
  ];

  // DIRECT COMPETITOR DISPLACEMENT MATRIX
  const competitorMatrix = [
    {
      provider: "SpaceByte Cloud",
      speed: "110+ MB/s (Sub-15ms IX)",
      fileLimit: "Uncapped (100GB+ single file)",
      aiScraping: "Zero AI Scraping Guarantee",
      dpdp: "100% Indian Tier-4 Hosted",
      perSeatTax: "None (Pooled Team Storage)",
      payment: "UPI, RuPay, INR Direct",
      isWinner: true,
    },
    {
      provider: "Google Drive / Google One",
      speed: "15 - 25 MB/s (Overseas RTT)",
      fileLimit: "Throttled / Browser Crashes",
      aiScraping: "Automated Scanning & Indexing",
      dpdp: "Overseas US/EU Data Centers",
      perSeatTax: "Per-User Google Workspace Fee",
      payment: "Credit Card + 3.5% Forex",
      isWinner: false,
    },
    {
      provider: "Dropbox",
      speed: "18 - 30 MB/s (High Latency)",
      fileLimit: "Sync Overhead on Multi-GB",
      aiScraping: "Third-party AI integrations",
      dpdp: "Foreign Cloud Servers",
      perSeatTax: "$15–$24/user/mo",
      payment: "USD Billing with Forex Markups",
      isWinner: false,
    },
    {
      provider: "Microsoft OneDrive / 365",
      speed: "12 - 20 MB/s (CPU Heavy Sync)",
      fileLimit: "Aggressive Upload Throttling",
      aiScraping: "Copilot Cloud Telemetry",
      dpdp: "Global Distributed Multi-Tenant",
      perSeatTax: "Per-Seat M365 Licensing",
      payment: "Recurring Card Subscriptions",
      isWinner: false,
    },
    {
      provider: "Proton Drive",
      speed: "8 - 15 MB/s (Swiss Encryption Lag)",
      fileLimit: "Strict File Upload Caps",
      aiScraping: "No AI Scraping",
      dpdp: "Swiss Jurisdiction (Not Indian)",
      perSeatTax: "Per-Seat Pricing",
      payment: "EUR/USD Billing Only",
      isWinner: false,
    },
    {
      provider: "TeraBox",
      speed: "3 - 8 MB/s (Aggressive Throttling)",
      fileLimit: "4GB Free File Limit",
      aiScraping: "Unclear Data Governance",
      dpdp: "Non-Compliant Foreign Servers",
      perSeatTax: "Ad-Supported or Premium",
      payment: "Third-party Gateways",
      isWinner: false,
    },
  ];

  // 11 PROFESSION SOLUTIONS (sb-roles)
  interface IndustryRole {
    num: string;
    icon: any;
    title: string;
    desc: React.ReactNode;
    url: string;
  }

  const INDUSTRY_ROLES: IndustryRole[] = [
    {
      num: "01",
      icon: Film,
      title: "Video Creators & Film Production",
      url: "https://spacebyte.in/cloud-storage-for-video-creators",
      desc: (
        <>
          Uncapped gigabit fiber upload speeds and zero transcoding for 4K/8K ProRes exports using{" "}
          <a href="/cloud-storage-for-video-creators" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Video Creators & Editors
          </a>.
        </>
      ),
    },
    {
      num: "02",
      icon: ImageIcon,
      title: "Photographers & Colorists",
      url: "https://spacebyte.in/cloud-storage-for-photographers",
      desc: (
        <>
          100% bit-for-bit RAW file preservation (.ARW, .CR3, .DNG) with full EXIF metadata retention via{" "}
          <a href="/cloud-storage-for-photographers" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Photographers
          </a>.
        </>
      ),
    },
    {
      num: "03",
      icon: YoutubeIcon,
      title: "YouTubers & Streamers",
      url: "https://spacebyte.in/cloud-storage-for-youtubers",
      desc: (
        <>
          Direct B-roll asset cataloging, lightning fast timeline transfers, and lifetime episode backups with{" "}
          <a href="/cloud-storage-for-youtubers" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for YouTubers
          </a>.
        </>
      ),
    },
    {
      num: "04",
      icon: Users,
      title: "Content Creators & Media Influencers",
      url: "https://spacebyte.in/cloud-storage-for-content-creators",
      desc: (
        <>
          Secure high-capacity storage for reels, sponsor deliverables, and brand footage using{" "}
          <a href="/cloud-storage-for-content-creators" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Content Creators
          </a>.
        </>
      ),
    },
    {
      num: "05",
      icon: Globe,
      title: "Digital Marketing Agencies",
      url: "https://spacebyte.in/cloud-storage-for-digital-marketing",
      desc: (
        <>
          Centralized client asset management with custom branded links and permission controls using{" "}
          <a href="/cloud-storage-for-digital-marketing" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Digital Marketing Agencies
          </a>.
        </>
      ),
    },
    {
      num: "06",
      icon: Key,
      title: "SEO Specialists & Data Analysts",
      url: "https://spacebyte.in/cloud-storage-for-seo-specialists",
      desc: (
        <>
          Fast distribution for massive crawl archives, technical site audit reports, and dataset backups with{" "}
          <a href="/cloud-storage-for-seo-specialists" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for SEO Specialists
          </a>.
        </>
      ),
    },
    {
      num: "07",
      icon: Sparkles,
      title: "Brand Creators & Advertising Studios",
      url: "https://spacebyte.in/cloud-storage-for-brand-creators",
      desc: (
        <>
          Organize master typography kits, vector libraries, and commercial brand design assets through{" "}
          <a href="/cloud-storage-for-brand-creators" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Brand Creators
          </a>.
        </>
      ),
    },
    {
      num: "08",
      icon: FolderKanban,
      title: "Designers & 3D Animators",
      url: "https://spacebyte.in/cloud-storage-for-designers",
      desc: (
        <>
          Effortless PSD, AI, Blender, and 3D project synchronization with zero sync lag on{" "}
          <a href="/cloud-storage-for-designers" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Designers
          </a>.
        </>
      ),
    },
    {
      num: "09",
      icon: Globe2,
      title: "Education & Academic Institutions",
      url: "https://spacebyte.in/cloud-storage-for-education",
      desc: (
        <>
          Secure lecture archives, curriculum sharing, and research collaboration vaults on{" "}
          <a href="/cloud-storage-for-education" className="text-[#00c6ff] font-bold hover:underline">
            Cloud Storage for Education
          </a>.
        </>
      ),
    },
    {
      num: "10",
      icon: Building2,
      title: "Corporate & Enterprise Vaults",
      url: "https://spacebyte.in/cloud-storage-for-enterprise",
      desc: (
        <>
          Multi-TB enterprise cloud backup solutions with DPDP compliance, zero AI scraping, and 99.99% uptime via{" "}
          <a href="/cloud-storage-for-enterprise" className="text-[#00c6ff] font-bold hover:underline">
            Secure Cloud Storage for Enterprise
          </a>.
        </>
      ),
    },
    {
      num: "11",
      icon: Database,
      title: "Small Business & Accounting Firms",
      url: "https://spacebyte.in/business-cloud-storage",
      desc: (
        <>
          Zero-knowledge encrypted client vaults for financial ledgers, audit records, and legal files on{" "}
          <a href="/business-cloud-storage" className="text-[#00c6ff] font-bold hover:underline">
            Business Cloud Storage in India
          </a>.
        </>
      ),
    },
  ];

  // 10 COMPREHENSIVE AEO / PAA FAQS
  const faqData = [
    {
      question: "Why is Google Drive upload so slow on high-speed internet in India?",
      answer:
        "Google Drive, Dropbox, and Microsoft OneDrive route user traffic through overseas servers located in Singapore, Europe, or the United States. This international routing introduces a Round-Trip Time (RTT) latency of 150ms or higher. On Indian high-speed fiber lines, this high latency triggers TCP window collapse, throttling transfer speeds down to 15–20 MB/s even on 1 Gbps connections. <strong>SpaceByte solves this</strong> by peering directly with domestic Indian Internet Exchanges (IX) in Mumbai, Delhi, and Bangalore, achieving sub-15ms latency and full line-speed saturation up to 110+ MB/s.",
    },
    {
      question: "Does SpaceByte scan or scrape my files to train artificial intelligence models?",
      answer:
        "<strong>No, absolutely never.</strong> SpaceByte maintains an enforceable Zero AI Scraping Policy. Unlike legacy hyperscalers whose revised terms allow automated scanners to parse private files for generative model training, SpaceByte employs zero-knowledge AES-256 encryption. Only you hold the cryptographic keys to decrypt your files, ensuring your intellectual property, client assets, and confidential records remain strictly private.",
    },
    {
      question: "What makes SpaceByte compliant with India's Digital Personal Data Protection (DPDP) Act?",
      answer:
        "India's DPDP Act 2023 mandates strict data residency, privacy safeguards, and breach reporting protocols for citizen and enterprise data. While foreign cloud providers store data across international multi-tenant servers subject to overseas jurisdiction, SpaceByte hosts 100% of user data strictly within certified Indian Tier-4 data centers. This guarantees sovereign data residency, eliminates cross-border legal liabilities, and ensures seamless compliance for Indian enterprises, law firms, and chartered accountants.",
    },
    {
      question: "How does SpaceByte preserve uncompressed RAW photos and 4K video exports?",
      answer:
        "Consumer cloud services like Google Photos or OneDrive run silent background transcoding engines that compress high-bitrate MP4/MOV files and strip EXIF camera metadata from RAW files (.ARW, .CR3, .DNG) to save server storage. SpaceByte enforces a strict <strong>Bit-for-Bit File Preservation Protocol</strong>. Your video exports, audio stems, color grading LUTs, and high-resolution camera archives are stored bit-by-bit identical to the studio master export.",
    },
    {
      question: "How do I bypass file size limits when uploading 50GB+ master video files?",
      answer:
        "Traditional web browser clients crash or trigger timeout errors when uploading files exceeding 15GB. SpaceByte utilizes an enterprise-grade multi-threaded chunking engine that fragments massive master files into encrypted parallel streams. This allows users to upload single files of 50GB, 100GB, or even 200GB+ without browser freezes, connection drops, or artificial file size caps.",
    },
    {
      question: "Can I share large deliverables without forcing my clients to create an account?",
      answer:
        "Yes! One of the biggest complaints with Google Drive and OneDrive is forced account creation and 'Download Quota Exceeded' error pages. SpaceByte allows you to generate custom branded, password-protected download links with custom expiry dates. Your clients can download multi-gigabyte deliverables instantly at full line speed with a single click—no login, no app installation, and no bandwidth quota limits.",
    },
    {
      question: "How does SpaceByte compare to Dropbox, OneDrive, and Proton Drive?",
      answer:
        "While Dropbox and OneDrive charge high per-seat fees ($15/mo per user) and struggle with high-latency Indian sync overhead, and Proton Drive suffers from slow upload throughput due to Swiss encryption hops, SpaceByte combines the best of all worlds: sub-15ms domestic Indian peering, zero-knowledge AES-256 privacy, zero AI scraping, pooled team capacity with zero per-seat taxes, and local pricing in Indian Rupees via UPI.",
    },
    {
      question: "Can I pay for cloud storage in India using UPI or RuPay without forex fees?",
      answer:
        "Yes! Foreign cloud providers bill in USD or process payments via international merchant gateways that trigger 3.5% foreign exchange markups and frequent OTP failures on Indian cards. SpaceByte is natively integrated with the Indian banking infrastructure, enabling instant, transparent payments via UPI (Google Pay, PhonePe, Paytm), RuPay, Indian Credit/Debit Cards, and Net Banking with zero forex markup.",
    },
    {
      question: "What is pooled cloud storage and why is it better than per-seat licensing?",
      answer:
        "Traditional enterprise platforms force companies to purchase a separate subscription for every employee or contractor ($15–$25/user/month), even if they only need occasional access. SpaceByte offers pooled team capacity where your entire organization or agency shares a centralized storage quota (e.g., 1TB, 5TB, or 10TB) with role-based access controls and zero per-seat surcharges.",
    },
    {
      question: "How can I migrate my files from Google Drive, OneDrive, or Dropbox to SpaceByte?",
      answer:
        "Migrating your files to SpaceByte Cloud is fast and seamless. You can upload existing archives directly through our high-throughput browser uploader or sync folders using desktop tools. Thanks to SpaceByte's domestic fiber connectivity, bulk migrations that typically take days on international services often complete in just a few hours.",
    },
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer.replace(/<\/?[^>]+(>|$)/g, "")
      }
    }))
  };

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // TESTIMONIALS DATA
  const reviews = useMemo(
    () => [
      {
        name: "Rahul Mehta",
        role: "Production House Lead",
        review: "Uploading 50GB 4K timelines to Google Drive used to stall at 18 MB/s and crash Chrome. With SpaceByte, our 1 Gbps office line hits 110+ MB/s consistently. Total game changer for video production in India.",
        avatar: "",
        rating: 5,
        accent: "#2563EB",
        light: "#EAF2FF",
      },
      {
        name: "Ananya Iyer",
        role: "Commercial Photographer",
        review: "Finally a cloud storage that doesn't compress my Sony ARW RAW files! Zero-knowledge encryption gives me complete peace of mind when backing up wedding shoots and client master galleries.",
        avatar: "",
        rating: 5,
        accent: "#7C3AED",
        light: "#F3E8FF",
      },
      {
        name: "Siddharth Rao",
        role: "Chartered Accountant & Partner",
        review: "Storing Indian client financial data on US servers was an ongoing audit risk under the DPDP Act. SpaceByte's Tier-4 Indian data center hosting and UPI billing solved our firm's compliance headache.",
        avatar: "",
        rating: 5,
        accent: "#0284C7",
        light: "#E0F2FE",
      },
      {
        name: "Tanvi Verma",
        role: "Agency Founder",
        review: "No more paying $15/month for interns and freelancers. SpaceByte's pooled team storage and custom branded client links make asset delivery look ultra-professional with zero download quota issues.",
        avatar: "",
        rating: 5,
        accent: "#2563EB",
        light: "#EAF2FF",
      },
    ],
    []
  );

  const totalReviews = reviews.length;

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold: 0.12 }
    );

    if (reviewRef.current) obs.observe(reviewRef.current);
    return () => obs.disconnect();
  }, []);

  const prevReview = () => setActiveIndex((p) => (p === 0 ? totalReviews - 1 : p - 1));
  const nextReview = () => setActiveIndex((p) => (p === totalReviews - 1 ? 0 : p + 1));

  useCustomStyles();

  return (
    <Fragment>
      <Helmet>
        <title>Best Secure Cloud Storage in India (2026) | SpaceByte</title>
        <meta
          name="description"
          content="Looking for the best secure cloud storage? Get end-to-end encryption, DPDP Act compliance, and 100% data privacy with SpaceByte's secure cloud storage solutions."
        />
        <meta
          name="keywords"
          content="best secure cloud storage, secure cloud storage india, secure online backup, zero knowledge cloud, private cloud storage"
        />
        <link rel="canonical" href="https://spacebyte.in/best-secure-cloud-storage" />
        <script type="application/ld+json" data-keep="true">
          {JSON.stringify(softwareSchema)}
        </script>
        <script type="application/ld+json" data-keep="true">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <LandingNavbar
        color="transparent"
        className="fixed top-0 z-50 w-full flex-shrink-0 border-none bg-light-primary shadow-lg"
        menuPosition="homepage-navbar"
      />

      {/* 1. HERO SECTION (SPACEBYTE SIGNATURE PATTERN) */}
      <section className="relative overflow-hidden !bg-gradient-to-r !from-[#0072ff]/8 !to-[#00c6ff]/8 py-24 md:py-32">
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid-sec-hero" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1E3A8A" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid-sec-hero)" />
          </svg>
        </div>

        <div className="landing-container z-10 flex flex-col items-center justify-between pb-40 pt-100">
          <div className="mx-auto w-full max-w-6xl px-4 text-center">
            
            {/* Trust Pill */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-200 text-[#0072ff] text-sm font-semibold mb-6 shadow-sm">
              <ShieldCheck className="w-4 h-4" />
              <span>India Tier-4 Hosted • DPDP Act Compliant • Zero AI Scraping</span>
            </div>

            {/* H1 Heading */}
            <h1 className="font-archivo text-[40px] md:text-[60px] font-bold leading-[1.2] tracking-[-0.02em] text-[#1f2937]">
              The Best Secure Cloud <br />
              <span className="color-primary-gradient">Storage in India</span>
            </h1>

            {/* Subheading */}
            <p className="mx-auto mt-30 max-w-3xl font-inter text-[18px] md:text-[20px] text-[#6b7280]">
              Eliminate overseas network latency, hidden per-seat licensing taxes, and Big Tech AI data scraping. 
              <strong> SpaceByte</strong> delivers ultra-fast <strong>secure cloud storage</strong> with sub-15ms domestic Indian peering, 
              zero-knowledge AES-256 encryption, 100% bit-for-bit RAW preservation, and transparent INR pricing with UPI billing.
            </p>

            {/* Signature CTA Buttons */}
            <div className="my-36 pb-20 flex flex-col items-center justify-center gap-6 sm:flex-row">
              <a
                href="/register"
                className="inline-flex items-center justify-center px-28 py-10 text-white font-bold bg-gradient-to-r from-[#0072ff] to-[#00c6ff] shadow-lg transition-all duration-300 hover:from-[#0059d6] hover:to-[#00aaff] hover:shadow-2xl hover:-translate-y-1"
                style={{ borderRadius: "12px" }}
              >
                Get 25 GB FREE
              </a>
              <a
                href="#Pricing"
                className="inline-flex items-center justify-center px-36 py-8 font-bold text-[#0072ff] relative transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                style={{
                  borderRadius: "12px",
                  background: "linear-gradient(#e5f5fb, #e5f5fb) padding-box, linear-gradient(90deg, #0072ff, #00c6ff) border-box",
                  border: "2px solid transparent"
                }}
              >
                View Pricing
              </a>
            </div>

            {/* Hero Dashboard Preview */}
            <div className="mt-26 flex justify-center px-2">
              <div className="w-full max-w-5xl overflow-hidden rounded-3xl bg-white shadow-2xl">
                <img
                  src="/images/LandingImg/heroimg.webp"
                  alt="SpaceByte Cloud Storage Dashboard"
                  className="h-auto w-full object-cover rounded-2xl"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* 2. THREE STEPS SECTION (SPACEBYTE SIGNATURE) */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-[#F8FAFC] to-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 landing-container z-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-[#3B82F6] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#06B6D4] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 my-30 md:my-50 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827] mb-10">
              Start Your Cloud Storage in <span className="color-primary-gradient">Three Steps</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto pb-10 font-archivo">
              Start storing, syncing, and sharing your files securely with domestic line-speed peering in minutes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative landing-container">
            {/* Connecting Line precisely centered behind the 80px circle icons */}
            <div className="hidden md:block absolute top-[40px] left-[18%] right-[18%] h-1 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#06B6D4] opacity-40 z-0"></div>

            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative z-10"
              >
                <div className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className="w-80 h-80 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center relative z-10 shadow-lg">
                      <step.icon className="w-40 h-40 text-white" />
                    </div>
                    <div className="absolute -top-2 -right-10 w-40 h-40 bg-white rounded-full flex items-center justify-center border-2 border-[#3B82F6] z-20">
                      <span className="text-[#1E3A8A] font-bold text-sm">{step.number}</span>
                    </div>
                  </div>

                  <h3 className="text-2xl font-semibold font-archivo text-[#111827] my-20">
                    {step.title}
                  </h3>
                  <p className="text-[#6B7280] font-archivo max-w-xs">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. OFFSET IMAGE SHOWCASE / BENEFIT SPLIT SECTION */}
      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 text-black lg:py-50">
        <div className="landing-container z-10 flex flex-col items-center justify-between gap-8 px-4 md:px-8 lg:flex-row">
          {/* Left Section with Offset Block */}
          <div className="relative w-full lg:w-1/2 flex justify-center">
            <div className="relative w-full max-w-[520px]">
              <div className="absolute -bottom-4 -right-4 h-full w-full rounded-2xl bg-gradient-to-br from-blue-600 to-blue-400" />
              <img
                src="/images/LandingImg/heroimg.webp"
                alt="Secure Cloud Storage Platform"
                className="relative rounded-2xl shadow-2xl w-full object-cover"
              />
            </div>
          </div>

          {/* Right Content */}
          <div className="w-full space-y-16 lg:w-1/2 pt-14 md:pt-0">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Why India Needs <span className="color-primary-gradient">Sovereign Cloud Storage</span>
            </h2>
            <p className="color-gray font-inter animate-fade-down leading-relaxed">
              Global cloud providers route your data through Singapore, European, and US data centers, adding 150ms+ latency and exposing sensitive Indian corporate and personal files to foreign surveillance laws. 
              <strong> SpaceByte Cloud</strong> delivers sub-15ms domestic Indian peering with 100% data residency in certified Indian Tier-4 data centers—fully compliant with the <strong>Digital Personal Data Protection (DPDP) Act 2023</strong> and guaranteed zero AI data scraping.
            </p>
          </div>
        </div>

        <div className="absolute -bottom-40 left-0 hidden lg:block">
          <img
            src="/images/contentcreators/background.png"
            className="h-auto w-320"
            alt="Decorative Shape"
          />
        </div>
      </section>

      {/* 4. SPEED & NETWORK BENCHMARK (OVERSEAS VS DOMESTIC PEERING) */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827]">
              Why Global Cloud Drives <span className="color-primary-gradient">Stall on Indian Fiber</span>
            </h2>
            <p className="mt-4 text-gray-600 font-inter text-base md:text-lg">
              Understanding TCP window collapse: why overseas routing destroys upload speed on gigabit connections, and how SpaceByte's domestic peering fixes it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 items-stretch">
            {/* OVERSEAS ROUTING BOX */}
            <div className="bg-[#f8fafc] rounded-2xl p-8 border border-red-200 shadow-sm flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-red-600 bg-red-50 px-3 py-1 rounded-full">
                    Legacy Cloud Architecture
                  </span>
                  <span className="text-sm font-semibold text-gray-500">Google Drive / Dropbox / OneDrive</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 font-archivo mb-3">
                  Overseas Hop Latency (150ms+ RTT)
                </h3>
                <p className="text-sm text-gray-600 font-inter leading-relaxed mb-6">
                  Traffic travels thousands of kilometers across submarine cables to data centers in Singapore, Europe, or the US. 
                  High Round-Trip Time forces TCP packets to wait for sequential acknowledgments, causing the TCP window to collapse.
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
                      <span>Upload Speed on 1 Gbps Line</span>
                      <span className="text-red-600 font-bold">15 - 20 MB/s (Throttled)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-red-500 h-3 rounded-full" style={{ width: '18%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
                      <span>Round-Trip Latency</span>
                      <span className="text-red-600 font-bold">140 - 180 ms</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-red-400 h-3 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>
                </div>

                <ul className="space-y-2 text-xs text-gray-600 font-inter">
                  <li className="flex items-center gap-2 text-red-600">
                    <X className="w-4 h-4 shrink-0" /> Browser crashes on single files &gt; 15GB
                  </li>
                  <li className="flex items-center gap-2 text-red-600">
                    <X className="w-4 h-4 shrink-0" /> Frequent timeout errors on large master video timelines
                  </li>
                </ul>
              </div>
            </div>

            {/* SPACEBYTE DOMESTIC BOX */}
            <div className="bg-gradient-to-br from-[#0072ff]/5 to-[#00c6ff]/10 rounded-2xl p-8 border-2 border-[#0072ff] shadow-md flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#0072ff] bg-blue-100 px-3 py-1 rounded-full">
                    SpaceByte Domestic Engine
                  </span>
                  <span className="text-sm font-bold text-[#0072ff]">Direct Indian Peering</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 font-archivo mb-3">
                  Domestic Fiber Saturation (&lt;15ms RTT)
                </h3>
                <p className="text-sm text-gray-600 font-inter leading-relaxed mb-6">
                  SpaceByte peers directly with major Indian Internet Exchanges (Extreme IX, NIXI) in Mumbai, Delhi, Bengaluru, and Chennai. 
                  Minimal packet transit distance maintains maximum TCP window size, allowing seamless gigabit line saturation.
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
                      <span>Upload Speed on 1 Gbps Line</span>
                      <span className="text-green-600 font-bold">110+ MB/s (Full Saturation)</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-gradient-to-r from-[#0072ff] to-[#00c6ff] h-3 rounded-full" style={{ width: '96%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-xs font-semibold text-gray-700 mb-1">
                      <span>Round-Trip Latency</span>
                      <span className="text-green-600 font-bold">&lt; 15 ms</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3">
                      <div className="bg-green-500 h-3 rounded-full" style={{ width: '12%' }}></div>
                    </div>
                  </div>
                </div>

                <ul className="space-y-2 text-xs text-gray-700 font-inter">
                  <li className="flex items-center gap-2 text-green-600 font-semibold">
                    <Check className="w-4 h-4 shrink-0" /> Uncapped single-file uploads (50GB+ master video exports)
                  </li>
                  <li className="flex items-center gap-2 text-green-600 font-semibold">
                    <Check className="w-4 h-4 shrink-0" /> Multi-threaded chunking engine prevents browser freezes
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. WHY SPACEBYTE IS THE BEST CHOICE (BIG GRADIENT ICON CARDS) */}
      <section className="relative overflow-hidden bg-[#f5f8ff] py-40 md:pt-60 pb-40">
        <div className="landing-container z-10 mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Why SpaceByte is the <span className="color-primary-gradient">Best Cloud Storage Choice</span>
            </h2>
            <p className="color-gray font-inter animate-fade-down py-16 md:py-24 max-w-3xl mx-auto">
              SpaceByte delivers <span className="color-primary-gradient font-bold">secure, fast, and scalable online storage</span> built to eliminate the real-world flaws of traditional legacy cloud platforms.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cards.map((card, index) => (
              <div
                key={index}
                className="group bg-white rounded-2xl p-14 md:p-16 lg:p-18 border border-gray-200 hover:border-[#3B82F6]/40 hover:shadow-xl transition-all duration-300 flex flex-col flex-1 min-h-[250px]"
              >
                <div
                  className={`w-40 h-40 md:w-50 md:h-50 bg-gradient-to-br ${card.color} rounded-xl flex items-center justify-center mb-12 transition-transform duration-300 group-hover:scale-110`}
                >
                  {card.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-8 font-archivo">
                  {card.title}
                </h3>
                <p className="color-gray font-base text-sm leading-relaxed flex-grow font-inter">
                  {card.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 6. ANIMATED ORBITING SECURITY SHIELD SECTION */}
      <section id="security" className="py-30 md:py-40 bg-gradient-to-br from-white to-[#F8FAFC] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#111827] mb-12 md:mb-16 font-archivo">
              Bank-Grade <span className="color-primary-gradient">Zero-Knowledge Security</span>
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto font-inter">
              Your files are protected with client-side zero-knowledge AES-256 encryption. Only you hold the decryption keys—guaranteeing 100% data sovereignty and zero AI data harvesting.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Rotating Orbiting Shield */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="relative">
                <div className="w-full max-w-md mx-auto aspect-square relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-3xl opacity-10 blur-3xl"></div>
                  <div className="relative w-full h-full flex items-center justify-center">
                    <div className="w-200 h-200 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-full flex items-center justify-center shadow-2xl">
                      <Shield className="w-100 h-100 text-white" />
                    </div>
                  </div>

                  {/* Orbiting Icons */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                    className="absolute inset-0"
                  >
                    {[Lock, Database, FileCheck, Users, Eye].map((Icon, index) => {
                      const angle = (index * 360) / 5;
                      const radius = 140;
                      const x = Math.cos((angle * Math.PI) / 180) * radius;
                      const y = Math.sin((angle * Math.PI) / 180) * radius;

                      return (
                        <motion.div
                          key={index}
                          animate={{ rotate: -360 }}
                          transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                          className="absolute top-1/2 left-1/2 w-40 h-40 bg-white rounded-xl shadow-lg flex items-center justify-center"
                          style={{
                            transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`,
                          }}
                        >
                          <Icon className="w-20 h-20 text-[#3B82F6]" />
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Right - Features List */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              {securityFeatures.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ x: 10 }}
                  className="flex items-start gap-6 group"
                >
                  <div className="w-50 h-50 bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                    <feature.icon className="w-11 h-11 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#111827] mb-3 ml-16 font-archivo">
                      {feature.title}
                    </h3>
                    <p className="text-[#6B7280] pb-10 ml-16 font-inter">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. FULL-WIDTH VIDEO SHOWCASE BANNER */}
      <section className="w-full px-4 md:px-10 py-40 bg-white relative overflow-hidden">
        <div className="landing-container min-h-[280px] md:min-h-[380px] flex items-center justify-center relative rounded-2xl overflow-hidden">
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/images/LandingImg/video.mp4"
            autoPlay
            loop
            muted
            playsInline
          />
          <div className="absolute inset-0 bg-black/60" />
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/20 via-transparent to-blue-600/20" />

          <div className="relative z-10 text-center max-w-4xl px-4">
            <h2 className="text-white text-3xl md:text-5xl font-semibold font-archivo">
              Your Data, Your Control, Your Cloud
            </h2>
            <p className="text-white/80 text-base md:text-lg mt-20 font-inter">
              A smarter cloud storage platform that gives you complete control over your data, uncapped gigabit file sharing, and digital privacy.
            </p>
            <div className="mt-20">
              <a href="/contact" className="btn bg-white text-black px-20 py-10 rounded-xl font-medium hover:scale-105 transition">
                <span>Contact Us</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. DIRECT COMPETITOR DISPLACEMENT MATRIX */}
      <section className="py-24 md:py-32 bg-[#F5F8FF] relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 landing-container">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-archivo font-bold text-[#111827]">
              Competitor Comparison: <span className="color-primary-gradient">SpaceByte vs. Legacy Drives</span>
            </h2>
            <p className="mt-3 text-gray-600 font-inter">
              Detailed technical benchmark comparing upload speeds, single-file upload caps, AI data scraping policies, DPDP compliance, and INR pricing.
            </p>
          </div>

          <div className="cmp-table-wrap">
            <table className="cmp-table">
              <thead>
                <tr>
                  <th className="cmp-th-feat">Cloud Storage Provider</th>
                  <th className="cmp-th-gdrive">India Upload Speed</th>
                  <th className="cmp-th-gdrive">Single-File Limit</th>
                  <th className="cmp-th-gdrive">AI Data Scraping</th>
                  <th className="cmp-th-gdrive">DPDP Compliance</th>
                  <th className="cmp-th-gdrive">Per-Seat Tax</th>
                  <th className="cmp-th-space">Billing Currency</th>
                </tr>
              </thead>
              <tbody>
                {competitorMatrix.map((row, idx) => (
                  <tr key={idx} className={row.isWinner ? "bg-[#eef6ff]" : ""}>
                    <td className="cmp-td-feat font-archivo flex items-center justify-between">
                      <span className="font-bold">{row.provider}</span>
                      {row.isWinner && <span className="cmp-winner-badge font-archivo ml-2">RECOMMENDED</span>}
                    </td>
                    <td className={`cmp-td-gdrive font-inter ${row.isWinner ? "font-bold text-[#0072ff]" : "text-gray-700"}`}>
                      {row.speed}
                    </td>
                    <td className="cmp-td-gdrive font-inter">{row.fileLimit}</td>
                    <td className="cmp-td-gdrive font-inter">
                      {row.isWinner ? (
                        <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-4 h-4" /> {row.aiScraping}</span>
                      ) : (
                        <span className="text-red-500 text-xs font-medium">{row.aiScraping}</span>
                      )}
                    </td>
                    <td className="cmp-td-gdrive font-inter">
                      {row.isWinner ? (
                        <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-4 h-4" /> {row.dpdp}</span>
                      ) : (
                        <span className="text-gray-500 text-xs">{row.dpdp}</span>
                      )}
                    </td>
                    <td className="cmp-td-gdrive font-inter">
                      {row.isWinner ? (
                        <span className="text-green-600 font-bold">{row.perSeatTax}</span>
                      ) : (
                        <span className="text-red-500 text-xs font-medium">{row.perSeatTax}</span>
                      )}
                    </td>
                    <td className="cmp-td-space font-inter font-bold">
                      {row.isWinner ? (
                        <span className="text-[#0072ff]">{row.payment}</span>
                      ) : (
                        <span className="text-gray-500 text-xs">{row.payment}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* 9. SOLUTIONS BY PROFESSION (THE SIGNATURE 2-COLUMN sb-roles SECTION) */}
      <section className="py-24 md:py-32 relative overflow-hidden bg-white">
        <div className="sb-roles landing-container z-10" id="roles">
          <div className="sb-roles-left">
            <div>
              <div className="sb-roles-eyebrow font-archivo">Industry Solutions</div>
              <h2 className="font-archivo text-2xl md:text-3xl font-bold">
                Tailored Storage Solutions for Every Industry
              </h2>
              <p className="font-inter mt-4 text-sm opacity-80 leading-relaxed">
                Discover high-performance cloud storage architectures built specifically for creators, agencies, corporate enterprises, and institutions.
              </p>
            </div>
            <div className="sb-rl-count font-archivo">
              11<br />
              <span>Solutions Linked</span>
            </div>
          </div>
          <div className="sb-roles-list">
            {INDUSTRY_ROLES.map((r) => (
              <div className="sb-role-row" key={r.num}>
                <div className="sb-rr-num font-archivo">{r.num}</div>
                <div className="sb-rr-icon">
                  <r.icon size={26} strokeWidth={2} className="text-[#1E3A8A]" />
                </div>
                <div className="sb-rr-body">
                  <div className="sb-rr-title font-archivo">{r.title}</div>
                  <div className="sb-rr-desc font-inter">{r.desc}</div>
                </div>
                <a href={r.url} target="_blank" rel="noopener noreferrer" className="sb-rr-arrow font-archivo">
                  →
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 10. PRICING TABLE SECTION */}
      <section id="Pricing" className="pricing-section relative overflow-hidden bg-white py-40 text-black lg:py-50">
        <div className="landing-container z-10">
          <div className="mx-auto max-w-xl space-y-16 text-center">
            <h2 className="animate-fade-down font-archivo text-3xl font-bold md:text-4xl lg:text-5xl">
              Affordable <span className="color-primary-gradient">Cloud Storage Pricing</span> Plans
            </h2>
            <p className="color-gray font-inter animate-fade-down">
              Get affordable pricing with no hidden fees, encrypted cloud storage, zero per-seat licensing taxes, and fast line-speed access anytime.
            </p>
          </div>

          <div className="my-40 flex flex-col md:flex-row justify-center items-center gap-16">
            <BillingCycleRadio
              products={query.data?.products}
              selectedCycle={selectedCycle}
              onChange={setSelectedCycle}
              className="input-radio animate-fade-up flex justify-center items-center flex-wrap"
              size="lg"
            />
          </div>

          <PricingTable
            selectedCycle={selectedCycle}
            productLoader="pricingPage"
            className="animate-fade-down"
          />

          {/* Premium Enterprise 5TB+ Card */}
          <div
            className="premium-bg animate-fade-down bg-light-primary relative mt-40 flex flex-col items-center justify-between space-y-8 overflow-hidden rounded-2xl border-primary p-16 shadow-lg md:flex-row md:space-y-0 md:p-32"
            style={{
              backgroundImage: "url('/images/LandingImg/ctamask.png'), url('/images/LandingImg/ctashape.png')",
              backgroundSize: 'contain, contain',
              backgroundRepeat: 'no-repeat, no-repeat',
              backgroundPosition: 'left center, right center',
            }}
          >
            <div className="font-archivo absolute -left-54 top-16 w-160 rotate-[-45deg] transform rounded bg-warning text-center text-sm font-medium text-black md:-left-56 lg:-left-50 lg:top-20">
              Premium
            </div>

            <div className="flex flex-col items-center gap-8 space-y-8 md:flex-row md:space-x-8 md:space-y-0">
              <img
                src="/images/LandingImg/cta-icon.webp"
                className="color-primary rounded-lg bg-white p-16"
                alt="CTA Icon"
              />
              <div className="text-center md:text-left">
                <h2 className="font-archivo text-3xl font-bold">5 TB or More?</h2>
                <span className="color-primary font-archivo text-2xl font-semibold">
                  ₹0.15/GB <span className="text-lg font-normal text-black">/Monthly</span>
                </span>
                <p className="font-inter mt-8 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> 100% Safe & Secure with End-to-End Encryption
                </p>
                <p className="font-inter mt-1 flex items-start justify-center gap-4 text-sm md:justify-start">
                  <CheckCircleOutlineIcon size="sm" /> Calculate the cost or Reach out to us
                </p>
              </div>
            </div>

            <div className="flex flex-row flex-wrap gap-8 sm:flex-col lg:flex-row justify-center">
              <div className="flex justify-center">{CalculateDialogTrigger()}</div>
              <div className="mt-0 flex justify-center">
                <a href="/contact" className="btn bg-[#f9fafb] border-2 border-[#0072ff] text-black hover:bg-[#0072ff] hover:text-white">
                  <span>Contact Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 11. THE SIGNATURE SPACEBYTE FAQ SECTION (ILLUSTRATION CARD + ACCORDION) */}
      <section id="faq" className="bg-[#f5f8ff] relative overflow-hidden py-24 text-black lg:py-32">
        <div className="landing-container z-10">
          <div className="section-wrapper">
            <div className="section-inner">
              {/* LEFT PANEL */}
              <div className="left-panel">
                <div className="badge font-archivo">
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <circle cx="5" cy="5" r="5" fill="#38bdf8" />
                  </svg>
                  SpaceByte Cloud
                </div>

                <h2 className="left-title font-archivo">
                  Best Cloud Storage <br />
                  Secure, Fast and <br />
                  Reliable
                </h2>

                <p className="left-sub font-archivo">
                  Everything you need to store, share, and access your files anytime, anywhere.
                </p>

                <div className="illustration">
                  <div className="illus-card">
                    <div className="illus-row">
                      <div className="illus-icon ic-blue">☁️</div>
                      <div className="illus-text font-archivo">
                        <strong>My Storage</strong>
                        <span>18.3 GB used of 25 GB</span>
                        <div className="illus-bar">
                          <div className="illus-bar-fill" style={{ width: "73%" }} />
                        </div>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-sky">📁</div>
                      <div className="illus-text font-archivo">
                        <strong>Shared Folders</strong>
                        <span>12 folders · 3 collaborators</span>
                      </div>
                    </div>

                    <div className="illus-row">
                      <div className="illus-icon ic-white">🔒</div>
                      <div className="illus-text font-archivo">
                        <strong>End-to-End Encrypted</strong>
                        <span>All files secured in transit</span>
                      </div>
                    </div>

                    <div className="stats-row font-archivo">
                      <div className="stat-chip">
                        <strong>25 GB</strong>
                        <span>Free Storage</span>
                      </div>
                      <div className="stat-chip">
                        <strong>99.9%</strong>
                        <span>Uptime</span>
                      </div>
                      <div className="stat-chip">
                        <strong>24/7</strong>
                        <span>Support</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* RIGHT PANEL (ACCORDION) */}
              <div className="right-panel">
                <p className="section-tag">Help Center & Search Q&A</p>

                <h2 className="section-title font-archivo">
                  Frequently Asked <br />
                  Questions
                </h2>

                <p className="section-desc font-inter">
                  Find clear answers to common questions about cloud storage in India, file upload speeds, and security standards.
                </p>

                <div className="accordion">
                  {faqData.map((item, index) => (
                    <div
                      key={index}
                      className={`acc-item ${openIndex === index ? "open" : ""}`}
                    >
                      <button className="acc-header" onClick={() => toggle(index)}>
                        <div className="acc-num font-archivo">
                          {String(index + 1).padStart(2, "0")}
                        </div>
                        <span className="acc-label font-archivo">{item.question}</span>
                        <div className="acc-chevron">
                          <svg viewBox="0 0 24 24" fill="none">
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </button>

                      <div className="acc-body">
                        <div
                          className="acc-body-inner font-inter"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 12. CUSTOMER REVIEWS / TESTIMONIALS SLIDER */}
      <section
        ref={reviewRef}
        className="relative overflow-hidden bg-white py-30 md:py-50"
      >
        <div className="pointer-events-none absolute -left-[180px] -top-[180px] h-[560px] w-[560px] rounded-full bg-[radial-gradient(circle,#dbeafe40,transparent_70%)]" />
        <div className="pointer-events-none absolute -bottom-[160px] -right-[160px] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle,#ede9fe40,transparent_70%)]" />

        <div className="relative z-[1] mx-auto grid max-w-[1280px] grid-cols-2 items-stretch gap-[56px] px-[clamp(20px,5vw,72px)] max-[900px]:grid-cols-1 max-[900px]:gap-[40px]">
          {/* LEFT CONTENT */}
          <div
            className={[
              "transition-all duration-700 ease-out",
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-7",
            ].join(" ")}
          >
            <h2 className="my-10 pt-6 md:pt-30 font-archivo text-[clamp(26px,3.2vw,46px)] font-extrabold leading-[1.15] tracking-[-0.025em] text-[#0f172a]">
              What Our{" "}
              <span className="bg-gradient-to-br font-archivo color-primary-gradient">
                Customers Say
              </span>
            </h2>

            <p className="mb-10 max-w-[440px] font-archivo text-[clamp(14px,1.1vw,16px)] leading-[1.82] text-[#64748b]">
              See how SpaceByte delivers secure file storage, high-speed domestic uploads, and advanced privacy protection. Read verified reviews from creators and enterprises.
            </p>

            {/* Controls */}
            <div className="flex flex-wrap my-20 items-center gap-3">
              <button
                onClick={prevReview}
                aria-label="Previous Review"
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowLeftIcon />
              </button>

              <button
                onClick={nextReview}
                aria-label="Next Review"
                className="flex h-[50px] w-[50px] items-center justify-center rounded-full border-[1.5px] border-[#e0e7ff] bg-white text-[#2563eb] shadow-[0_2px_12px_rgba(37,99,235,0.09)] transition-all duration-200 ease-out hover:scale-[1.08] hover:border-[#2563eb] hover:bg-[#2563eb] hover:text-white hover:shadow-[0_8px_24px_rgba(37,99,235,0.28)]"
              >
                <ArrowRightIcon />
              </button>
            </div>

            {/* Dots */}
            <div className="ml-[6px] flex gap-[6px] my-16">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={[
                    "h-[8px] rounded-[4px] transition-all duration-300",
                    i === activeIndex ? "w-[26px] bg-[#2563eb]" : "w-[8px] bg-[#cbd5e1]",
                  ].join(" ")}
                />
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mt-5 h-[3px] max-w-[220px] overflow-hidden rounded bg-[#e2e8f0]">
              <div
                className="h-full rounded bg-gradient-to-r from-[#2563eb] to-[#7c3aed] transition-all duration-500"
                style={{ width: `${((activeIndex + 1) / totalReviews) * 100}%` }}
              />
            </div>
          </div>

          {/* RIGHT SLIDER */}
          <div
            className={[
              "w-full transition-all duration-700 ease-out delay-200",
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-7",
            ].join(" ")}
          >
            <div className="relative w-full overflow-hidden">
              <div
                className="flex transition-transform duration-700 ease-in-out"
                style={{
                  transform: `translateX(-${activeIndex * 100}%)`,
                }}
              >
                {reviews.map((item, i) => (
                  <div key={i} className="w-full flex-shrink-0 px-3">
                    <ReviewCard item={item} isActive={i === activeIndex} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 13. FINAL CONVERSION CTA BANNER */}
      <section className="py-20 md:py-32 bg-gradient-to-br from-[#1E3A8A] via-[#3B82F6] to-[#60A5FA] relative overflow-hidden">
        <div className="absolute inset-0 landing-container z-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="cta-grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#cta-grid)" />
          </svg>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-40 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="inline-flex items-center justify-center w-60 h-60 bg-white/20 backdrop-blur-sm rounded-2xl mb-12"
            >
              <Sparkles className="w-30 h-30 text-white" />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white font-archivo"
            >
              Ready to Secure Your Data?
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="text-lg sm:text-xl text-white/90 my-16 max-w-2xl mx-auto font-inter"
            >
              Claim your 25GB permanent free cloud storage today. Test sub-15ms line-speed domestic uploads. No credit card required.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-10 justify-center items-center"
            >
              <a href="/register" className="group bg-white text-[#1E3A8A] px-16 py-14 rounded-xl font-semibold hover:bg-gray-100 transition-all hover:shadow-2xl hover:shadow-white/20 transform hover:-translate-y-1 flex items-center gap-2 btn">
                Get 25 GB FREE - No Card Needed
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 14. CLASSIC SPACEBYTE DARK NAVY FOOTER */}
      <footer className="Footer-section relative overflow-hidden bg-[#0F172A] text-white pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-2 md:col-span-4 lg:col-span-2">
              <div className="space-y-4 mr-12 padding-logo padding-top">
                <a href="https://spacebyte.in/" className="flex-shrink-0" aria-label="SpaceByte Homepage">
                  <img
                    src="/images/logo-light.png"
                    alt="SpaceByte Logo"
                    className="site-logo"
                  />
                </a>
                <div>
                  <p className="font-medium pt-8 pb-10 font-archivo">SPACEBYTE CLOUD STORAGE</p>
                  <p className="pb-10 pt-3 flex items-center gap-2 font-inter text-xs text-gray-400">
                    311, Maruti Plaza, Sardar Patel Chowk, Krishnanagar, Ahmedabad, Gujarat, India - 382345
                  </p>
                  <p className="pb-10 pt-3 flex items-center gap-2 font-inter text-xs text-gray-400">
                    Mo. +91 97257 71457
                  </p>
                </div>
                <div>
                  <p className="font-bold text-base pb-10 pt-3 font-archivo">FOLLOW US FOR "SPACEBYTE.IN"</p>
                  <div className="flex gap-4 mt-3 logg mb-10">
                    <ul className="flex space-x-16 pt-3 pr-10">
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.facebook.com/profile.php?id=61567307277313&mibextid=ZbWKwL" target="_blank" rel="noopener noreferrer">
                          <FacebookIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://www.instagram.com/spacebyte.in?igsh=MmZidnprMXZtbTho" target="_blank" rel="noopener noreferrer">
                          <InstagramIcon className="text-white" />
                        </a>
                      </li>
                      <li className="bg-light rounded-lg p-8">
                        <a href="https://youtube.com/@spacebytecloudstorage?si=lt2mfOM7kSaN3g6B" target="_blank" rel="noopener noreferrer">
                          <YoutubeIcon className="text-white" />
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Column */}
            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start font-archivo">Product</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start font-inter">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#Pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#security" className="hover:text-white transition-colors">Security</a></li>
                <li><a href="#roles" className="hover:text-white transition-colors">Solutions</a></li>
              </ul>
            </div>

            {/* Company Column */}
            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start font-archivo">Company</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start font-inter">
                <li><a href="/about-us" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="/pages/articles" className="hover:text-white transition-colors">Articles</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact Us</a></li>
              </ul>
            </div>

            {/* Resources Column */}
            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start font-archivo">Resources</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start font-inter">
                <li><a href="#faq" className="hover:text-white transition-colors">FAQs</a></li>
                <li><a href="/smart-google-drive-alternative" className="hover:text-white transition-colors">Google Drive Alternative</a></li>
                <li><a href="/dropbox-alternative-fast-secure-cloud-storage" className="hover:text-white transition-colors">Dropbox Alternative</a></li>
              
                <li>
                  <a href="/storage/sitemaps/sitemap-index.xml" className="hover:text-white transition-colors">
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal Column */}
            <div className="py-8 md:py-20 px-30">
              <h2 className="font-semibold text-xl mb-10 text-start font-archivo">Legal</h2>
              <ul className="space-y-3 text-sm text-gray-400 text-start font-inter">
                <li><a href="/pages/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/pages/terms-of-service" className="hover:text-white transition-colors">Terms of Service</a></li>
                <li><a href="/pages/refund-policy" className="hover:text-white transition-colors">Refund Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="font-inter border-t border-t-primary/80 py-16 text-center mt-5">
            <p className="text-sm text-gray-400">
              Copyright © {new Date().getFullYear()}{' '}
              <a href="/login" className="color-primary-gradient font-semibold">
                SpaceByte Cloud Storage
              </a>
              , All Rights Reserved. Hosted in Tier-4 Indian Data Centers.
            </p>
          </div>
        </div>
      </footer>
    </Fragment>
  );
}

function ReviewCard({
  item,
  isActive,
}: {
  item: any;
  isActive: boolean;
}) {
  const { name, role, avatar, review, accent, light, rating } = item;
  const initial = name?.charAt(0).toUpperCase() || "U";

  return (
    <div
      className={[
        "relative flex-shrink-0 overflow-hidden m-10 rounded-[28px] border-[1.5px] p-[clamp(24px,3.5vw,42px)] transition-all duration-500 flex flex-col justify-between",
        isActive ? "translate-y-[-4px] scale-[1.015]" : "scale-[0.97]",
      ].join(" ")}
      style={{
        minWidth: "clamp(270px, 42vw, 450px)",
        maxWidth: "clamp(270px, 42vw, 450px)",
        background: isActive
          ? `linear-gradient(150deg, ${light} 0%, white 100%)`
          : `linear-gradient(150deg, ${light}88 0%, #f8fafc 100%)`,
        borderColor: isActive ? `${accent}50` : "#e2e8f0",
        boxShadow: isActive
          ? `0 24px 60px ${accent}1a, 0 4px 16px ${accent}10`
          : "0 2px 12px rgba(0,0,0,0.05)",
        opacity: isActive ? 1 : 0.68,
      }}
    >
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="flex gap-1">
            {[...Array(5)].map((_, i) => (
              <span key={i} style={{ color: i < rating ? accent : "#cbd5e1" }} className="text-[18px]">
                ★
              </span>
            ))}
          </div>
          <span className="font-serif text-[60px] leading-none opacity-10 absolute right-8 top-6" style={{ color: accent }}>
            “
          </span>
        </div>

        <p className="font-archivo text-[clamp(14px,1.15vw,16px)] font-medium leading-[1.7] mb-8 text-[#334155]">
          "{review}"
        </p>
      </div>

      <div className="flex items-center gap-10">
        <div 
          className="flex h-50 w-50 shrink-0 items-center justify-center rounded-full border-2 font-archivo text-[20px] font-bold"
          style={{ 
            borderColor: `${accent}40`,
            background: isActive ? `${accent}15` : "#f1f5f9",
            color: accent 
          }}
        >
          {avatar ? (
            <img src={avatar} alt={name} className="h-full w-full object-cover rounded-full" />
          ) : (
            <span>{initial}</span> 
          )}
        </div>

        <div className="overflow-hidden">
          <h4 className="truncate font-archivo text-[17px] font-bold text-[#0f172a] leading-tight">
            {name}
          </h4>
          <p className="truncate text-[13px] font-semibold" style={{ color: accent }}>
            {role}
          </p>
        </div>
      </div>

      <div
        className="absolute bottom-0 left-0 h-[3px] rounded-bl-[28px] transition-all duration-[650ms] ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          width: isActive ? "100%" : "0%",
          background: `linear-gradient(90deg, ${accent}, ${accent}44)`,
        }}
      />
    </div>
  );
}

function CalculateDialogTrigger() {
  const form = useForm<ContactPagePayload>({
    defaultValues: {
      message: '',
    },
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const submitForm = useSubmitContactForm(form);
  const { verify } = useRecaptcha('contact');

  return (
    <>
      <DialogTrigger type="modal" isOpen={isOpen} onOpenChange={setIsOpen}>
        <button className="btn w-full rounded-lg !text-white font-semibold !bg-gradient-to-r !from-[#0072ff] !to-[#00c6ff] shadow-lg hover:shadow-xl transition px-6 py-3">
          <span>Get Quote</span>
        </button>
        <Dialog className="w-full max-w-lg rounded-xl p-6">
          <DialogHeader className="font-archivo text-2xl font-semibold" showDivider>
            <Trans message="Storage Cost Calculator" />
          </DialogHeader>
          <DialogBody>
            <div className="font-inter flex items-center justify-center text-center text-sm text-gray-600">
              Enter storage capacity in TB (greater than 5 TB) to calculate your monthly cost.
            </div>
            <Form
              form={form}
              className="mt-4 flex flex-col items-center text-center"
              onSubmit={async (value) => {
                calculateCost(value);
              }}
            >
              <FormTextField
                label={<Trans message="Enter Storage in TB" />}
                name="tb"
                required
                type="number"
                className="mb-4 w-full rounded-lg p-2"
              />
              <div className="text-red-500 text-sm font-semibold" id="errorMsg"></div>
              <div className="text-blue-600 text-sm font-bold mt-2" id="resultOutput"></div>
              <div className="mt-6 flex w-full justify-between gap-4">
                <button className="btn bg-[#0072ff] text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition" type="submit">
                  Calculate
                </button>
                <Button
                  color="positive"
                  variant="outline"
                  className="rounded-lg"
                  type="button"
                  onClick={() => {
                    const resultOutput = document.getElementById('resultOutput')?.textContent;
                    form.setValue('message', resultOutput || '');
                    setIsOpen(false);
                    setIsContactDialogOpen(true);
                  }}
                >
                  Contact Us
                </Button>
              </div>
            </Form>
          </DialogBody>
        </Dialog>
      </DialogTrigger>

      {isContactDialogOpen && (
        <DialogTrigger type="modal" isOpen={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
          <Dialog className="w-full max-w-lg rounded-xl p-6">
            <DialogHeader className="font-archivo text-2xl font-semibold" showDivider>
              <Trans message="Contact Us" />
            </DialogHeader>
            <DialogBody>
              {submitForm.isPending ? (
                <FullPageLoader />
              ) : (
                <Form
                  form={form}
                  className="flex flex-col gap-4"
                  onSubmit={async (value) => {
                    const isValid = await verify();
                    if (isValid) {
                      submitForm.mutate(value, {
                        onSuccess: () => {
                          setIsContactDialogOpen(false);
                        },
                      });
                    }
                  }}
                >
                  <FormTextField label={<Trans message="Name" />} name="name" required className="rounded-lg p-2" />
                  <FormTextField label={<Trans message="Email" />} name="email" required type="email" className="rounded-lg p-2" />
                  <FormTextField label={<Trans message="Mobile Number" />} name="mobileNumber" required type="number" className="rounded-lg p-2" />
                  <FormTextField
                    label={<Trans message="Message" />}
                    name="message"
                    required
                    inputElementType="textarea"
                    className="rounded-lg p-2"
                    rows={4}
                  />

                  <div className="mt-4 flex w-full justify-between gap-4">
                    <Button variant="flat" onClick={() => setIsContactDialogOpen(false)} className="rounded-lg px-4 py-2 text-sm">
                      <Trans message="Back" />
                    </Button>
                    <Button type="submit" variant="flat" color="primary" className="rounded-lg px-4 py-2 text-sm font-semibold">
                      <Trans message="Send Message" />
                    </Button>
                  </div>
                </Form>
              )}
            </DialogBody>
          </Dialog>
        </DialogTrigger>
      )}
    </>
  );
}

function calculateCost({ tb }: any) {
  const tbInput: any = tb;
  const errorMsg: any = document.getElementById('errorMsg');
  const resultOutput: any = document.getElementById('resultOutput');

  if (!errorMsg || !resultOutput) return;

  errorMsg.textContent = '';
  resultOutput.textContent = '';

  if (!/^\d+$/.test(tbInput)) {
    errorMsg.textContent = 'Error: Storage must be a whole number!';
    return;
  }

  const tbNumber = parseInt(tbInput, 10);

  if (tbNumber <= 5) {
    errorMsg.textContent = 'Error: Storage must be greater than 5 TB!';
    return;
  }

  const gbStorage = tbNumber * 1024;
  const cost = gbStorage * 0.15;
  resultOutput.innerHTML = `
    Storage: <strong>${tbNumber.toLocaleString()} TB</strong><br>
    Total Cost: <strong>₹${cost.toFixed(2)}/month</strong><br>
    GST and Convenience Fee Extra**
  `;
}

function useCustomStyles() {
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .cmp-table-wrap {
        overflow-x: auto;
        border-radius: 16px;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
        border: 1px solid #e5e7eb;
        background: #fff;
      }
      .cmp-table {
        width: 100%;
        border-collapse: collapse;
        text-align: left;
      }
      .cmp-table th {
        background: #0072ff;
        color: #fff;
        padding: 16px 20px;
        font-family: 'Archivo', sans-serif;
        font-size: 14px;
        font-weight: 700;
        white-space: nowrap;
      }
      .cmp-table td {
        padding: 16px 20px;
        border-bottom: 1px solid #e5e7eb;
        font-size: 14px;
      }
      .cmp-winner-badge {
        background: #10B981;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        padding: 2px 8px;
        border-radius: 9999px;
        letter-spacing: 0.05em;
      }
      .sb-roles {
        display: grid;
        grid-template-columns: 280px 1fr;
        border-bottom: 2px solid #1E3A8A;
        border-top: 2px solid #1E3A8A;
        min-height: 420px;
        background: #fff;
      }
      .sb-roles-left {
        border-right: 2px solid #1E3A8A;
        padding: 3.5rem 2.5rem;
        background: #1E3A8A;
        color: #FDFCFA;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      .sb-roles-eyebrow {
        font-size: .7rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .14em;
        color: #00c2ff;
        margin-bottom: 1.2rem;
      }
      .sb-roles-left h2 {
        font-size: 2rem;
        font-weight: 900;
        line-height: 1.1;
        letter-spacing: -.5px;
        color: #FDFCFA;
      }
      .sb-roles-left p {
        font-size: .85rem;
        color: rgba(253,252,250,0.55);
        margin-top: 1rem;
        line-height: 1.65;
      }
      .sb-rl-count {
        font-size: 3.5rem;
        font-weight: 900;
        color: #00c2ff;
        line-height: 1;
        margin-top: 2rem;
      }
      .sb-rl-count span {
        display: block;
        font-size: .78rem;
        font-weight: 500;
        color: rgba(253,252,250,0.4);
        letter-spacing: .1em;
        text-transform: uppercase;
        margin-top: .3rem;
      }
      .sb-roles-list {
        display: flex;
        flex-direction: column;
        background: #f5f8ff;
      }
      .sb-role-row {
        display: flex;
        align-items: center;
        border-bottom: 1px solid #e2e8f0;
        cursor: pointer;
        transition: background .2s;
      }
      .sb-role-row:last-child {
        border-bottom: none;
      }
      .sb-role-row:hover {
        background: #e3eaf9;
      }
      .sb-role-row:hover .sb-rr-arrow {
        opacity: 1;
        transform: translateX(0);
      }
      .sb-rr-num {
        width: 60px;
        flex-shrink: 0;
        border-right: 1px solid #e2e8f0;
        text-align: center;
        padding: 1.8rem 0;
        font-size: .75rem;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: .08em;
        color: #64748b;
      }
      .sb-rr-icon {
        width: 64px;
        flex-shrink: 0;
        border-right: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1.8rem 0;
      }
      .sb-rr-body {
        flex: 1;
        padding: 1.8rem 2rem;
        min-width: 0;
      }
      .sb-rr-title {
        font-size: .95rem;
        font-weight: 700;
        color: #0f172a;
        margin-bottom: .25rem;
      }
      .sb-rr-desc {
        font-size: .8rem;
        color: #64748b;
        line-height: 1.5;
      }
      .sb-rr-arrow {
        padding: 0 1.5rem;
        font-size: 1.1rem;
        color: #3B82F6;
        opacity: 0;
        transform: translateX(-6px);
        transition: opacity .2s, transform .2s;
        flex-shrink: 0;
      }
      @media (max-width: 860px) {
        .sb-roles {
          grid-template-columns: 1fr;
        }
        .sb-roles-left {
          border-right: none;
          border-bottom: 2px solid #1E3A8A;
          padding: 2.5rem 2rem;
        }
      }
      @media (max-width: 560px) {
        .sb-rr-num { width: 42px; font-size: .65rem; }
        .sb-rr-icon { width: 48px; }
        .sb-rr-body { padding: 1.2rem 1rem; }
        .sb-rr-arrow { display: none; }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
}
